// src/pages/dashboard/Settings.js
// COMPLETE SETTINGS FILE WITH ALLOWANCE AND DEDUCTION TYPES MANAGEMENT ADDED

import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Form,
  Input,
  Button,
  Select,
  InputNumber,
  TimePicker,
  Switch,
  DatePicker,
  Modal,
  Table,
  Divider,
  message,
  Typography,
  Space,
  Alert,
  Radio,
  Row,
  Col,
  Popconfirm,
  Grid,
  Skeleton,
  Tooltip
} from 'antd';
import {
  BuildOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  TeamOutlined,
  CalendarOutlined,
  BankOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  BgColorsOutlined,
  SettingOutlined,
  GiftOutlined,
  MinusCircleOutlined
} from '@ant-design/icons';
import { supabase } from '../../services/supabase';
import { useTheme } from '../../context/ThemeContext';
import { themes } from '../../utils/themeConfig';
import { departmentService } from '../../services/departmentService';
import AllowanceTypesManagement from './Settings/AllowanceTypesManagement';
import DeductionTypesManagement from './Settings/DeductionTypesManagement';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import { Page, PageHeader, StatusPill } from '../../components/ui';

const SETTINGS_TABS = [
  'company', 'appearance', 'working-hours', 'payroll', 'allowance-types',
  'deduction-types', 'leave', 'department-leave', 'departments', 'holidays',
];

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

const Settings = () => {
  // Open a specific tab from the URL, e.g. /dashboard/settings?tab=holidays
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(SETTINGS_TABS.includes(requestedTab) ? requestedTab : 'company');
  const screens = Grid.useBreakpoint();

  const handleTabChange = (key) => {
    setActiveTab(key);
    setSearchParams(key === 'company' ? {} : { tab: key }, { replace: true });
  };

  // State declarations
  const [loading, setLoading] = useState(false);
  const [companyId, setCompanyId] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [themeLoading, setThemeLoading] = useState(false);

  // Department leave settings state
  const [departments, setDepartments] = useState([]);
  const [showDeptLeaveModal, setShowDeptLeaveModal] = useState(false);
  const [editingDeptLeave, setEditingDeptLeave] = useState(null);

  // Department management state
  const [showDeptModal, setShowDeptModal] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [employees, setEmployees] = useState([]);

  // Theme context
  const { currentTheme, changeTheme, getCurrentTheme } = useTheme();

  // Form instances
  const [companyForm] = Form.useForm();
  const [workingHoursForm] = Form.useForm();
  const [payrollForm] = Form.useForm();
  const [leaveForm] = Form.useForm();
  const [holidayForm] = Form.useForm();
  const [deptLeaveForm] = Form.useForm();
  const [departmentForm] = Form.useForm();

  // Initialization
  useEffect(() => {
    initializeSettings();
  }, []);

  // Fetch data after initialization
  useEffect(() => {
    if (companyId && initialized) {
      fetchSettings();
      fetchHolidays();
      fetchDepartments();
      fetchEmployees();
    }
  }, [companyId, initialized]);

  const initializeSettings = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        message.error('Please log in to continue');
        return;
      }

      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (companyError) throw companyError;

      if (!companyData) {
        message.error('No company found');
        return;
      }

      console.log('Company ID set:', companyData.id);
      setCompanyId(companyData.id);
      setInitialized(true);
    } catch (error) {
      console.error('Initialization error:', error);
      message.error('Failed to initialize settings');
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch employees (for counting)
  const fetchEmployees = async () => {
    try {
      const { data, error } = await supabase
        .from('employees')
        .select('id, first_name, last_name, department')
        .eq('company_id', companyId);

      if (error) throw error;
      setEmployees(data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  // Function to fetch departments with leave settings
  const fetchDepartments = async () => {
    try {
      const data = await departmentService.getDepartments(companyId);
      setDepartments(data || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
      message.error('Failed to fetch departments');
    }
  };

  // Department management functions
  const handleAddDepartment = () => {
    setEditingDepartment(null);
    departmentForm.resetFields();
    setShowDeptModal(true);
  };

  const handleEditDepartment = (department) => {
    setEditingDepartment(department);
    departmentForm.setFieldsValue({
      name: department.name,
      description: department.description
    });
    setShowDeptModal(true);
  };

  const handleSaveDepartment = async (values) => {
    try {
      setLoading(true);

      if (editingDepartment) {
        // Update existing department
        await departmentService.updateDepartment(
          editingDepartment.id,
          values,
          companyId
        );
        message.success('Department updated successfully');
      } else {
        // Add new department
        await departmentService.addDepartment(values, companyId);
        message.success('Department added successfully');
      }

      setShowDeptModal(false);
      setEditingDepartment(null);
      departmentForm.resetFields();
      fetchDepartments();
      fetchEmployees(); // Refresh employee count
    } catch (error) {
      console.error('Error saving department:', error);
      message.error('Failed to save department');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDepartment = async (id) => {
    try {
      await departmentService.deleteDepartment(id, companyId);
      message.success('Department deleted successfully');
      fetchDepartments();
      fetchEmployees();
    } catch (error) {
      console.error('Error deleting department:', error);
      message.error('Failed to delete department');
    }
  };

  const handleSaveDeptLeave = async (values) => {
    try {
      setLoading(true);

      const leaveSettings = {
        sick_leave_days: values.sick_leave_days,
        local_leave_days: values.local_leave_days || 0
      };

      if (editingDeptLeave) {
        await departmentService.updateDepartmentLeaveSettings(
          editingDeptLeave.id,
          leaveSettings,
          companyId
        );
        message.success('Department leave settings updated successfully');
      } else {
        message.error('Department not found');
        return;
      }

      setShowDeptLeaveModal(false);
      setEditingDeptLeave(null);
      deptLeaveForm.resetFields();
      fetchDepartments();
    } catch (error) {
      console.error('Error saving department leave settings:', error);
      message.error('Failed to save department leave settings');
    } finally {
      setLoading(false);
    }
  };

  const handleEditDeptLeave = (department) => {
    setEditingDeptLeave(department);
    deptLeaveForm.setFieldsValue({
      department_name: department.name,
      sick_leave_days: department.sick_leave_days || 7,
      local_leave_days: department.local_leave_days || 0
    });
    setShowDeptLeaveModal(true);
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);

      // Fetch company info with exact column names from your table
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select(`
          name,
          logo_url,
          address,
          contact_email,
          contact_phone,
          business_type,
          registration_number,
          tax_number,
          website,
          is_active
        `)
        .eq('id', companyId)
        .single();

      if (companyError) {
        console.error('Company fetch error:', companyError);
        throw companyError;
      }

      console.log('Company data fetched:', companyData);

      // Set company form values
      companyForm.setFieldsValue({
        company_name: companyData?.name,
        logo_url: companyData?.logo_url,
        address: companyData?.address,
        email: companyData?.contact_email,
        phone: companyData?.contact_phone,
        business_type: companyData?.business_type,
        registration_number: companyData?.registration_number,
        tax_number: companyData?.tax_number,
        website: companyData?.website
      });

      // Fetch other settings
      const { data: settings, error: settingsError } = await supabase
        .from('settings')
        .select('*')
        .eq('company_id', companyId)
        .single();

      if (settingsError && settingsError.code !== 'PGRST116') throw settingsError;

      // Format working hours before setting form values
      const workingHours = settings?.working_hours || {};
      workingHoursForm.setFieldsValue({
        ...workingHours,
        office_start_time: workingHours.office_start_time ? dayjs(workingHours.office_start_time, 'HH:mm') : null,
        office_end_time: workingHours.office_end_time ? dayjs(workingHours.office_end_time, 'HH:mm') : null,
        work_week: workingHours.work_week || [],
        regular_hours: workingHours.regular_hours || 8,
        grace_period_minutes: workingHours.grace_period_minutes || 15,
        break_duration_minutes: workingHours.break_duration_minutes || 60
      });

      // Set payroll form values with defaults for new fields
      const payrollSettings = settings?.payroll_settings || {};
      payrollForm.setFieldsValue({
        ...payrollSettings,
        overtime_calculation_method: payrollSettings.overtime_calculation_method || 'daily',
        monthly_overtime_threshold: payrollSettings.monthly_overtime_threshold || 195,
        overtime_rate: payrollSettings.overtime_rate || 1.5,
        double_overtime_rate: payrollSettings.double_overtime_rate || 2.0,
        double_overtime_threshold: payrollSettings.double_overtime_threshold || 10,
        holiday_rate: payrollSettings.holiday_rate || 2.0,
        payroll_start_date: payrollSettings.payroll_start_date || 1,
        payroll_end_date: payrollSettings.payroll_end_date || 30,
        enable_auto_processing: payrollSettings.enable_auto_processing || false
      });

      // Set leave form values
      leaveForm.setFieldsValue(settings?.leave_settings || {});
    } catch (error) {
      console.error('Settings fetch error:', error);
      message.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchHolidays = async () => {
    try {
      const { data, error } = await supabase
        .from('holidays')
        .select('*')
        .eq('company_id', companyId)
        .order('date');

      if (error) throw error;
      setHolidays(data || []);
    } catch (error) {
      console.error('Holidays fetch error:', error);
      message.error('Failed to fetch holidays');
    }
  };

  const handleSave = async (values, settingType) => {
    try {
      if (!companyId) {
        message.error('Company ID is not set');
        return;
      }

      setLoading(true);

      // Format the time values if this is working_hours settings
      let formattedValues = { ...values };
      if (settingType === 'working_hours') {
        formattedValues = {
          ...values,
          office_start_time: values.office_start_time?.format('HH:mm'),
          office_end_time: values.office_end_time?.format('HH:mm')
        };
      }

      // Check if settings exist
      const { data: existingSettings } = await supabase
        .from('settings')
        .select('id')
        .eq('company_id', companyId)
        .single();

      let error;

      if (existingSettings) {
        const { error: updateError } = await supabase
          .from('settings')
          .update({
            [settingType]: formattedValues,
            updated_at: new Date().toISOString()
          })
          .eq('company_id', companyId);
        error = updateError;
      } else {
        const { error: insertError } = await supabase
          .from('settings')
          .insert([{
            company_id: companyId,
            [settingType]: formattedValues,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }]);
        error = insertError;
      }

      if (error) throw error;
      message.success('Settings saved successfully');
    } catch (error) {
      console.error('Settings save error:', error);
      message.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCompanySettings = async (values) => {
    try {
      setLoading(true);
      const { error } = await supabase
        .from('companies')
        .update({
          name: values.company_name,
          logo_url: values.logo_url,
          address: values.address,
          contact_email: values.email,
          contact_phone: values.phone,
          business_type: values.business_type,
          registration_number: values.registration_number,
          tax_number: values.tax_number,
          website: values.website,
          updated_at: new Date().toISOString()
        })
        .eq('id', companyId);

      if (error) throw error;
      message.success('Company settings saved successfully');
    } catch (error) {
      console.error('Company settings save error:', error);
      message.error('Failed to save company settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveHoliday = async (values) => {
    try {
      if (!companyId) {
        message.error('Company ID is not set');
        return;
      }

      setLoading(true);
      const holidayData = {
        company_id: companyId,
        name: values.name,
        date: values.date.format('YYYY-MM-DD'),
        description: values.description || '',
        is_recurring: values.is_recurring || false
      };

      const { error } = await supabase
        .from('holidays')
        .insert([holidayData]);

      if (error) throw error;

      message.success('Holiday added successfully');
      setShowHolidayModal(false);
      holidayForm.resetFields();
      fetchHolidays();
    } catch (error) {
      console.error('Holiday save error:', error);
      message.error('Failed to save holiday');
    } finally {
      setLoading(false);
    }
  };

  const handleEditHoliday = (holiday) => {
    setEditingHoliday(holiday);
    holidayForm.setFieldsValue({
      ...holiday,
      date: dayjs(holiday.date),
    });
    setShowHolidayModal(true);
  };

  const handleDeleteHoliday = async (id) => {
    try {
      const { error } = await supabase
        .from('holidays')
        .delete()
        .eq('id', id)
        .eq('company_id', companyId);

      if (error) throw error;
      message.success('Holiday deleted successfully');
      fetchHolidays();
    } catch (error) {
      console.error('Holiday delete error:', error);
      message.error('Failed to delete holiday');
    }
  };

  // Theme handling functions
  const handleThemeChange = async (themeName) => {
    try {
      setThemeLoading(true);
      await changeTheme(themeName);
      message.success(`Theme changed to ${themes[themeName].name}`);
    } catch (error) {
      console.error('Error changing theme:', error);
      message.error('Failed to change theme');
    } finally {
      setThemeLoading(false);
    }
  };

  // Theme option: a miniature of the app drawn in the theme's own colours
  const ThemePreview = ({ themeData, isSelected, onClick }) => {
    const c = themeData.colors;
    return (
      <button type="button" className="theme-option" aria-pressed={isSelected} onClick={onClick}>
        <span className="theme-option__preview" style={{ background: c.pageBackground }} aria-hidden="true">
          <span className="theme-option__side" style={{ background: c.sidebarBackground }}>
            <span className="theme-option__bar" style={{ background: c.activeBackground }} />
            <span className="theme-option__bar" style={{ background: c.text, opacity: 0.18 }} />
            <span className="theme-option__bar" style={{ background: c.text, opacity: 0.18 }} />
          </span>
          <span className="theme-option__main" style={{ background: c.cardBackground }}>
            <span className="theme-option__bar" style={{ background: c.text, opacity: 0.55, width: '45%' }} />
            <span className="theme-option__row">
              <span className="theme-option__tile" style={{ background: '#ffffff', opacity: 0.7 }} />
              <span className="theme-option__tile" style={{ background: '#ffffff', opacity: 0.7 }} />
            </span>
            <span className="theme-option__bar" style={{ background: c.primary, width: '30%', height: 9 }} />
          </span>
        </span>
        <span className="theme-option__meta">
          <span>
            <span className="theme-option__name">{themeData.name}</span>
            <span className="theme-option__desc">{themeData.description}</span>
          </span>
          <span className="theme-option__swatches" aria-hidden="true">
            {[c.primary, c.secondary, c.activeBackground, c.danger].map((color, i) => (
              <i key={i} style={{ background: color }} />
            ))}
          </span>
        </span>
        {isSelected && <span className="chip" style={{ alignSelf: 'flex-start' }}>Current theme</span>}
      </button>
    );
  };

  // Columns for department leave settings table
  const deptLeaveColumns = [
    {
      title: 'Department',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Sick Leave Days',
      dataIndex: 'sick_leave_days',
      key: 'sick_leave_days',
      render: (days) => days || 7, // Default value
    },
    {
      title: 'Local Leave Days',
      dataIndex: 'local_leave_days',
      key: 'local_leave_days',
      render: (days) => days || 0, // Default value
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<SettingOutlined />}
            onClick={() => handleEditDeptLeave(record)}
          >
            Configure leave
          </Button>
        </Space>
      ),
    },
  ];

  // Loading state check
  const pageHeader = (
    <PageHeader
      title="Settings"
      subtitle="Company details, working hours, payroll rules, leave, departments, holidays and appearance."
    />
  );

  if (!initialized || !companyId) {
    return (
      <Page>
        {pageHeader}
        <Card>
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      </Page>
    );
  }

  return (
    <Page className="settings-page">
      {pageHeader}

      <Tabs
        className="settings-tabs"
        activeKey={activeTab}
        onChange={handleTabChange}
        tabPosition={screens.lg ? 'left' : 'top'}
      >
        {/* Company Settings Tab */}
        <TabPane
          tab={<span><BankOutlined /> Company</span>}
          key="company"
        >
          <Card>
            <Form
              className="settings-form"
              form={companyForm}
              layout="vertical"
              onFinish={handleSaveCompanySettings}
            >
              <Title level={4}>Company Information</Title>
              <Form.Item name="company_name" label="Company Name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="logo_url" label="Logo URL">
                <Input />
              </Form.Item>
              <Form.Item name="business_type" label="Business Type">
                <Input />
              </Form.Item>
              <Form.Item name="registration_number" label="Registration Number">
                <Input />
              </Form.Item>
              <Form.Item name="tax_number" label="Tax Number">
                <Input />
              </Form.Item>
              <Form.Item name="website" label="Website">
                <Input />
              </Form.Item>
              <Form.Item name="address" label="Address">
                <Input.TextArea />
              </Form.Item>
              <Form.Item name="email" label="Contact Email">
                <Input type="email" />
              </Form.Item>
              <Form.Item name="phone" label="Contact Phone">
                <Input />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={loading}>
                Save Company Settings
              </Button>
            </Form>
          </Card>
        </TabPane>

        <TabPane
          tab={<span><BgColorsOutlined /> Appearance</span>}
          key="appearance"
        >
          <Card>
            <Title level={4}>Theme Settings</Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
              Choose a theme that suits your preference. Changes are applied immediately and saved to your profile.
            </Text>

            <Row gutter={[16, 16]}>
              {Object.entries(themes).map(([themeName, themeData]) => (
                <Col xs={24} sm={12} xl={8} key={themeName}>
                  <ThemePreview
                    themeName={themeName}
                    themeData={themeData}
                    isSelected={currentTheme === themeName}
                    onClick={() => handleThemeChange(themeName)}
                  />
                </Col>
              ))}
            </Row>

            {themeLoading && (
              <Alert
                message="Applying Theme"
                description="Please wait while we apply your new theme..."
                type="info"
                showIcon
                style={{ marginTop: 16 }}
              />
            )}

            <Divider />

            <div className="soft-panel">
              <span className="kv-list__label">Current theme</span>
              <span className="kv-list__value">{getCurrentTheme().name}</span>
              <Text type="secondary" style={{ fontSize: 12.5 }}>
                {getCurrentTheme().description}
              </Text>
            </div>
          </Card>
        </TabPane>

        {/* Working Hours Tab */}
        <TabPane
          tab={<span><ClockCircleOutlined /> Working Hours</span>}
          key="working-hours"
        >
          <Card>
            <Form
              className="settings-form"
              form={workingHoursForm}
              layout="vertical"
              onFinish={(values) => handleSave(values, 'working_hours')}
            >
              <Title level={4}>Working Hours Configuration</Title>
              <Form.Item name="regular_hours" label="Regular Hours Per Day" rules={[{ required: true }]}>
                <InputNumber min={1} max={24} />
              </Form.Item>
              <Form.Item name="office_start_time" label="Office Start Time">
                <TimePicker
                  format="HH:mm"
                  minuteStep={15}
                />
              </Form.Item>

              <Form.Item name="office_end_time" label="Office End Time">
                <TimePicker
                  format="HH:mm"
                  minuteStep={15}
                />
              </Form.Item>
              <Form.Item name="grace_period_minutes" label="Grace Period (Minutes)">
                <InputNumber min={0} max={60} />
              </Form.Item>
              <Form.Item name="break_duration_minutes" label="Break Duration (Minutes)">
                <InputNumber min={0} max={120} />
              </Form.Item>
              <Form.Item name="work_week" label="Working Days">
                <Select mode="multiple">
                  <Option value="1">Monday</Option>
                  <Option value="2">Tuesday</Option>
                  <Option value="3">Wednesday</Option>
                  <Option value="4">Thursday</Option>
                  <Option value="5">Friday</Option>
                  <Option value="6">Saturday</Option>
                  <Option value="0">Sunday</Option>
                </Select>
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={loading}>
                Save Working Hours
              </Button>
            </Form>
          </Card>
        </TabPane>

        <TabPane
          tab={<span><DollarOutlined /> Payroll</span>}
          key="payroll"
        >
          <Card>
            <Form
              className="settings-form"
              form={payrollForm}
              layout="vertical"
              onFinish={(values) => handleSave(values, 'payroll_settings')}
            >
              <Title level={4}>Payroll Configuration</Title>

              <Divider orientation="left">Overtime Calculation Method</Divider>
              <Form.Item
                name="overtime_calculation_method"
                label="How Overtime is Calculated"
                rules={[{ required: true }]}
              >
                <Radio.Group>
                  <Space direction="vertical">
                    <Radio value="daily">
                      <Text strong>Daily Basis</Text> - Calculate overtime when daily hours exceed the regular hours threshold
                    </Radio>
                    <Radio value="monthly">
                      <Text strong>Monthly Total</Text> - Calculate overtime when total monthly hours exceed a threshold
                    </Radio>
                    <Radio value="simple">
                      <Text strong>Simple (Aux Champs Elysee)</Text> - Simple calculation with automatic leave handling and recurring allowances/deductions
                    </Radio>
                  </Space>
                </Radio.Group>
              </Form.Item>

              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.overtime_calculation_method !== currentValues.overtime_calculation_method
                }
              >
                {({ getFieldValue }) => {
                  const calculationMethod = getFieldValue('overtime_calculation_method');

                  if (calculationMethod === 'monthly') {
                    return (
                      <Form.Item
                        name="monthly_overtime_threshold"
                        label="Monthly Overtime Threshold (hours)"
                        tooltip="Total working hours per month before overtime applies"
                        rules={[{ required: true }]}
                      >
                        <InputNumber min={1} style={{ width: 200 }} />
                      </Form.Item>
                    );
                  }

                  return null;
                }}
              </Form.Item>

              <Divider orientation="left">Overtime Settings</Divider>
              <Form.Item name="overtime_rate" label="Regular Overtime Rate Multiplier" rules={[{ required: true }]}>
                <InputNumber min={1} step={0.1} placeholder="e.g., 1.5 for 150% of regular pay" />
              </Form.Item>

              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.overtime_calculation_method !== currentValues.overtime_calculation_method
                }
              >
                {({ getFieldValue }) => {
                  const calculationMethod = getFieldValue('overtime_calculation_method');

                  if (calculationMethod === 'daily') {
                    return (
                      <>
                        <Form.Item name="double_overtime_rate" label="Double Overtime Rate Multiplier" rules={[{ required: true }]}>
                          <InputNumber min={1} step={0.1} placeholder="e.g., 2.0 for 200% of regular pay" />
                        </Form.Item>

                        <Form.Item
                          name="double_overtime_threshold"
                          label="Double Overtime Threshold (hours)"
                          tooltip="Total hours after which double overtime rate applies. For example, if regular hours is 8 and you want double OT after 2 hours of regular OT, set this to 10."
                          rules={[{ required: true }]}
                        >
                          <InputNumber min={1} step={0.5} />
                        </Form.Item>
                      </>
                    );
                  }

                  return null;
                }}
              </Form.Item>

              <Form.Item name="holiday_rate" label="Holiday/Weekend Rate Multiplier" rules={[{ required: true }]}>
                <InputNumber min={1} step={0.1} />
              </Form.Item>

              <Divider orientation="left">Pay Period</Divider>
              <Form.Item name="payroll_start_date" label="Payroll Start Date">
                <Select>
                  {[...Array(28)].map((_, i) => (
                    <Option key={i + 1} value={i + 1}>{i + 1}</Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="payroll_end_date" label="Payroll End Date">
                <Select>
                  {[...Array(31)].map((_, i) => (
                    <Option key={i + 1} value={i + 1}>{i + 1}</Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="enable_auto_processing" valuePropName="checked">
                <Switch checkedChildren="Auto Processing On" unCheckedChildren="Auto Processing Off" />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={loading}>
                Save Payroll Settings
              </Button>
            </Form>
          </Card>
        </TabPane>

        {/* Allowance Types Tab */}
        <TabPane
          tab={<span><GiftOutlined /> Allowance Types</span>}
          key="allowance-types"
        >
          <AllowanceTypesManagement companyId={companyId} />
        </TabPane>

        {/* Deduction Types Tab */}
        <TabPane
          tab={<span><MinusCircleOutlined /> Deduction Types</span>}
          key="deduction-types"
        >
          <DeductionTypesManagement companyId={companyId} />
        </TabPane>

        {/* Leave Settings Tab */}
        <TabPane
          tab={<span><CalendarOutlined /> Leave</span>}
          key="leave"
        >
          <Card>
            <Form
              className="settings-form"
              form={leaveForm}
              layout="vertical"
              onFinish={(values) => handleSave(values, 'leave_settings')}
            >
              <Title level={4}>Global Leave Configuration</Title>
              <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                These are the default leave settings. Individual departments can override these values.
              </Text>
              <Form.Item name="annual_leave_days" label="Default Annual Leave Days" rules={[{ required: true }]}>
                <InputNumber min={0} />
              </Form.Item>
              <Form.Item name="sick_leave_days" label="Default Sick Leave Days" rules={[{ required: true }]}>
                <InputNumber min={0} />
              </Form.Item>
              <Form.Item name="carry_forward_days" label="Maximum Carry Forward Days">
                <InputNumber min={0} />
              </Form.Item>
              <Form.Item name="auto_approve_leave" valuePropName="checked">
                <Switch checkedChildren="Auto Approve On" unCheckedChildren="Auto Approve Off" />
              </Form.Item>
              <Form.Item name="require_attachment_sick_leave" valuePropName="checked">
                <Switch checkedChildren="Attachment Required" unCheckedChildren="No Attachment" />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={loading}>
                Save Leave Settings
              </Button>
            </Form>
          </Card>
        </TabPane>

        {/* Department Leave Settings Tab */}
        <TabPane
          tab={<span><TeamOutlined /> Department Leave Settings</span>}
          key="department-leave"
        >
          <Card>
            <Title level={4}>Department-Specific Leave Configuration</Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
              Configure different leave allowances for each department. These settings will override the global leave settings for employees in specific departments.
            </Text>

            <Alert
              message="Important Note"
              description="Changes to department leave settings will affect payroll calculations. Employees who exceed their department's leave allowance will have deductions applied to their salary."
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Table
              columns={deptLeaveColumns}
              dataSource={departments}
              rowKey="id"
              loading={loading}
              pagination={false}
            />
          </Card>

          {/* Department Leave Settings Modal */}
          <Modal
            title={`Configure Leave Settings - ${editingDeptLeave?.name}`}
            open={showDeptLeaveModal}
            onCancel={() => {
              setShowDeptLeaveModal(false);
              setEditingDeptLeave(null);
              deptLeaveForm.resetFields();
            }}
            footer={null}
            width={600}
          >
            <Form
              form={deptLeaveForm}
              layout="vertical"
              onFinish={handleSaveDeptLeave}
            >
              <Form.Item
                name="department_name"
                label="Department"
              >
                <Input disabled />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="sick_leave_days"
                    label="Sick Leave Days"
                    rules={[{ required: true, message: 'Please enter sick leave days' }]}
                  >
                    <InputNumber min={0} max={365} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="local_leave_days"
                    label="Local Leave Days"
                    rules={[{ required: true, message: 'Please enter local leave days' }]}
                  >
                    <InputNumber min={0} max={365} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              <Alert
                message="Leave Policy Example"
                description={
                  <div>
                    <p><strong>SHOP Department:</strong> 15 sick days + 22 local days</p>
                    <p><strong>STITCHING Department:</strong> 13 sick days + 18 local days</p>
                    <p>If an employee exceeds their department's allowance, salary deductions will be applied automatically during payroll processing.</p>
                  </div>
                }
                type="info"
                style={{ marginBottom: 16 }}
              />

              <Form.Item>
                <Space>
                  <Button onClick={() => setShowDeptLeaveModal(false)}>
                    Cancel
                  </Button>
                  <Button type="primary" htmlType="submit" loading={loading}>
                    Save Leave Settings
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Modal>
        </TabPane>

        {/* Departments Tab */}
        <TabPane
          tab={<span><TeamOutlined /> Departments</span>}
          key="departments"
        >
          <Card>
            <Title level={4}>Department Management</Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
              Manage your company departments. Individual leave policies can be configured in the "Department Leave Settings" tab.
            </Text>

            <div style={{ marginBottom: 16 }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddDepartment}
              >
                Add Department
              </Button>
            </div>

            <Table
              columns={[
                {
                  title: 'Department Name',
                  dataIndex: 'name',
                  key: 'name',
                },
                {
                  title: 'Description',
                  dataIndex: 'description',
                  key: 'description',
                },
                {
                  title: 'Employee Count',
                  key: 'employee_count',
                  render: (_, record) => {
                    // Count employees in this department
                    return employees.filter(emp => emp.department === record.name).length;
                  },
                },
                {
                  title: 'Actions',
                  key: 'actions',
                  render: (_, record) => (
                    <Space>
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => handleEditDepartment(record)}
                      >
                        Edit
                      </Button>
                      <Popconfirm
                        title="Are you sure you want to delete this department?"
                        onConfirm={() => handleDeleteDepartment(record.id)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                        >
                          Delete
                        </Button>
                      </Popconfirm>
                    </Space>
                  ),
                },
              ]}
              dataSource={departments}
              rowKey="id"
              loading={loading}
              pagination={false}
            />
          </Card>

          {/* Department Modal */}
          <Modal
            title={editingDepartment ? "Edit Department" : "Add Department"}
            open={showDeptModal}
            onCancel={() => {
              setShowDeptModal(false);
              setEditingDepartment(null);
              departmentForm.resetFields();
            }}
            footer={null}
          >
            <Form
              form={departmentForm}
              layout="vertical"
              onFinish={handleSaveDepartment}
            >
              <Form.Item
                name="name"
                label="Department Name"
                rules={[{ required: true, message: 'Please enter department name' }]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="description"
                label="Description"
              >
                <Input.TextArea rows={4} />
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button onClick={() => setShowDeptModal(false)}>
                    Cancel
                  </Button>
                  <Button type="primary" htmlType="submit" loading={loading}>
                    {editingDepartment ? 'Update' : 'Add'} Department
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Modal>
        </TabPane>

        {/* Public Holidays Tab */}
        <TabPane
          tab={<span><CalendarOutlined /> Public Holidays</span>}
          key="holidays"
        >
          <Card>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div className="flex justify-between items-center">
                <Title level={4}>Public Holidays</Title>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setShowHolidayModal(true)}
                >
                  Add Holiday
                </Button>
              </div>

              <Table
                columns={[
                  {
                    title: 'Holiday Name',
                    dataIndex: 'name',
                    key: 'name',
                  },
                  {
                    title: 'Date',
                    dataIndex: 'date',
                    key: 'date',
                    render: (date) => dayjs(date).format('DD/MM/YYYY'),
                    sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix(),
                  },
                  {
                    title: 'Description',
                    dataIndex: 'description',
                    key: 'description',
                  },
                  {
                    title: 'Recurring',
                    dataIndex: 'is_recurring',
                    key: 'is_recurring',
                    render: (isRecurring) => (
                      isRecurring ? <StatusPill tone="positive">Every year</StatusPill> : <span className="text-muted">One-time</span>
                    ),
                  },
                  {
                    title: 'Actions',
                    key: 'actions',
                    render: (_, record) => (
                      <Space size={2}>
                        <Tooltip title="Edit holiday">
                          <Button
                            type="text"
                            className="icon-action"
                            data-tone="primary"
                            aria-label="Edit holiday"
                            icon={<EditOutlined />}
                            onClick={() => handleEditHoliday(record)}
                          />
                        </Tooltip>
                        <Popconfirm
                          title="Delete this holiday?"
                          onConfirm={() => handleDeleteHoliday(record.id)}
                          okText="Delete"
                          okButtonProps={{ danger: true }}
                          cancelText="Cancel"
                        >
                          <Tooltip title="Delete holiday">
                            <Button
                              type="text"
                              className="icon-action"
                              data-tone="danger"
                              aria-label="Delete holiday"
                              icon={<DeleteOutlined />}
                            />
                          </Tooltip>
                        </Popconfirm>
                      </Space>
                    ),
                  },
                ]}
                dataSource={holidays}
                rowKey="id"
              />
            </Space>
          </Card>

          {/* Holiday Form Modal */}
          <Modal
            title={editingHoliday ? "Edit Holiday" : "Add Holiday"}
            open={showHolidayModal}
            onCancel={() => {
              setShowHolidayModal(false);
              setEditingHoliday(null);
              holidayForm.resetFields();
            }}
            footer={null}
          >
            <Form
              form={holidayForm}
              layout="vertical"
              onFinish={handleSaveHoliday}
              initialValues={{ is_recurring: false }}
            >
              <Form.Item
                name="name"
                label="Holiday Name"
                rules={[{ required: true, message: 'Please enter holiday name' }]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="date"
                label="Date"
                rules={[{ required: true, message: 'Please select date' }]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                />
              </Form.Item>

              <Form.Item
                name="description"
                label="Description"
              >
                <Input.TextArea rows={4} />
              </Form.Item>

              <Form.Item
                name="is_recurring"
                valuePropName="checked"
              >
                <Switch checkedChildren="Recurring" unCheckedChildren="One-time" />
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button onClick={() => setShowHolidayModal(false)}>
                    Cancel
                  </Button>
                  <Button type="primary" htmlType="submit" loading={loading}>
                    {editingHoliday ? 'Update' : 'Add'} Holiday
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Modal>
        </TabPane>
      </Tabs>
    </Page>
  );
};

export default Settings;